import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Switch,
  Divider,
  Tag,
} from "antd";
import GenericTable from "../../components/Commons/Table/generic-table.component";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import ShowDataLength from "../../components/Commons/Table/results-length-table.component";
import { useEffect, useState } from "react";
import { getAll, remove, importSales } from "../../services/sales.service";
import { create as createInvoice } from "../../services/invoices.service";
import { PaginationProps } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import { useNavigate } from "react-router-dom";
import { AlertService } from "../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import ConfirmModal from "../../components/Commons/Modal/confirm-modal.component";
import ProfilePermissionService from "../../services/profilePermissions.service";
import { isMobile } from "react-device-detect";
import { formatCurrency } from "../../services/utils";
import { paymentMethods } from "../../Enums/PaymentMethod.enums";
import { salesStatus } from "../../Enums/SalesStatus.enums";
import ExportBtn from "../../components/Commons/Export/ExportButton";
import { downloadExcel } from "../../services/utils";
import moment from "moment";
import FilterButton from "../../components/Commons/Table/FilterButton";
import { getAll as loadCustomers } from "../../services/customers.service";
import { RangePickerProps } from "antd/lib/date-picker";
import DetailSale from "./Detail";
import ImportBtn from "../../components/Commons/Import/ImportButton";
import { getAccountDetails } from "../../services/accounts.service";
import dayjs from "dayjs";

const { Search } = Input;

export default function List() {
  const [sales, setSales] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number | undefined>(10);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<string>("created_at-desc");
  const [removeSale, setRemoveSale] = useState<any | undefined>(undefined);
  const [customers, setCustomers] = useState<any[]>([]);
  const [customerId, setCustomerId] = useState<any>();
  const [paymentMethod, setPaymentMethod] = useState<any>();
  const [startDate, setStartDate] = useState<string>(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string>(
    moment().endOf("month").format("YYYY-MM-DD")
  );
  const [hasContractOnly, setHasContractOnly] = useState<boolean>(false);
  const [salesToExport, setSalesToExport] = useState([]);
  const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [details, setDetails] = useState<any>(null);
  const [generateInvoiceLoading, setGenerateInvoiceLoading] =
    useState<any>(undefined);
  const [allowedPaymentMethods, setAllowedPaymentMethods] =
    useState<any[]>(paymentMethods);

  const [formSearch] = Form.useForm();
  const [formFilter] = Form.useForm();

  const navigate = useNavigate();

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { isLoading: saleLoading, refetch: loadSales } = useQuery(
    [
      "loadSales",
      currentPage,
      search,
      order,
      perPage,
      customerId,
      paymentMethod,
      startDate,
      endDate,
      hasContractOnly,
    ],
    () =>
      getAll({
        page: currentPage,
        per_page: perPage,
        order,
        search,
        customer_id: Number(customerId),
        payment_method: paymentMethod,
        has_contract: hasContractOnly ? "1" : undefined,
        period: startDate && endDate ? `${startDate}, ${endDate}` : undefined,
        date: startDate && !endDate ? startDate : undefined,
      }),
    {
      onSuccess: (response) => {
        setSales(
          response.data.map((sale: any) => {
            return {
              key: sale.data.id,
              ...sale.data,
            };
          })
        );

        setSalesToExport([]);
        setCurrentPage(response.meta.current_page);
        setPerPage(response.meta.per_page);
        setTotal(response.meta.total);
      },
      refetchOnWindowFocus: false,
    }
  );

  useQuery(["loadCustomers"], () => loadCustomers({ status: "active" }), {
    onSuccess: (response) => {
      setCustomers(
        response.data.map((customer: any) => {
          return {
            key: customer.data.id,
            value: customer.data.id,
            label: customer.data.name,
          };
        })
      );
    },
    refetchOnWindowFocus: false,
  });

  useQuery(["getAccountDetails"], () => getAccountDetails(), {
    onSuccess: (response) => {
      const settings =
        typeof response?.data?.settings === "string"
          ? JSON.parse(response?.data?.settings)
          : response?.data?.settings || {};
      const allowed = paymentMethods.filter((m) =>
        (settings?.payment_methods || []).includes(m.value)
      );
      setAllowedPaymentMethods(allowed.length ? allowed : paymentMethods);
    },
    refetchOnWindowFocus: false,
  });

  const { refetch: loadSalesToExport } = useQuery(
    ["loadSalesToExport"],
    () =>
      getAll({
        order,
        search,
        customer_id: Number(customerId),
        payment_method: paymentMethod,
        has_contract: hasContractOnly ? "1" : undefined,
        period: startDate && endDate ? `${startDate}, ${endDate}` : undefined,
        date: startDate && !endDate ? startDate : undefined,
      }),
    {
      onSuccess: (response) =>
        setSalesToExport(onMapDataToExport(response.data)),
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  const { mutate: removeSaleMutate } = useMutation(remove, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: "Venda apagada com sucesso." }]);

      setRemoveSale(undefined);

      if (sales.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        loadSales();
      }
    },
    onError: () => setRemoveSale(undefined),
  });

  const { mutate: generateInvoicesMutate } = useMutation(createInvoice, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: "Fatura gerada com sucesso." }]);

      loadSales();
      setGenerateInvoiceLoading(undefined);
    },
    onError: () => setGenerateInvoiceLoading(undefined),
  });

  const onSearch = (search_value: any) => {
    setCurrentPage(1);
    setSearch(search_value);
  };

  const onFilter = () => {
    setCurrentPage(1);
    setCustomerId(formFilter.getFieldValue("filter_customer"));
    setPaymentMethod(formFilter.getFieldValue("filter_payment_method"));
    setHasContractOnly(!!formFilter.getFieldValue("filter_has_contract"));

    const rangeDate = formFilter.getFieldValue("filter_date_range");

    if (rangeDate && rangeDate.length === 2) {
      setStartDate(rangeDate[0].format("YYYY-MM-DD"));
      setEndDate(rangeDate[1].format("YYYY-MM-DD"));
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const onClearFilters = () => {
    setCurrentPage(1);
    setCustomerId("");
    setPaymentMethod("");
    setStartDate("");
    setEndDate("");
    formFilter.setFieldValue("filter_date_range", null);
    setHasContractOnly(false);
    formFilter.setFieldValue("filter_has_contract", false);
  };

  const disabledStartDate: RangePickerProps["disabledDate"] = (current) => {
    let endDate = formFilter.getFieldValue("filter_end_date");

    if (endDate) {
      return current >= endDate;
    }

    return false;
  };

  const disabledEndDate: RangePickerProps["disabledDate"] = (current) => {
    let startDate = formFilter.getFieldValue("filter_start_date");

    if (startDate) {
      return current <= startDate;
    }

    return false;
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    if (pagination.current != currentPage) {
      setCurrentPage(Number(pagination.current));
    }

    if (pagination.pageSize != perPage) {
      setPerPage(pagination.pageSize);
      setCurrentPage(1);
    }

    if (sorter.order) {
      let orderBy = `${sorter.field}-${sorter.order.substr(
        0,
        sorter.order.length - 3
      )}`;

      if (order != orderBy) {
        setOrder(orderBy);
      }
    }
  };

  const toPage = (page: string, sale_uuid: string = "") => {
    let pages = {
      create: `/sales/create`,
      edit: `/sales/${sale_uuid}/edit`,
      duplicate: `/sales/${sale_uuid}/duplicate`,
    };

    navigate(pages[page as keyof typeof pages]);
  };

  const onOpenDetails = (record: any) => {
    setDetails(record);
    setOpenDetails(true);
  };

  const onCloseDetails = () => {
    setOpenDetails(false);
    setDetails(null);
  };

  const columns: any = [
    {
      title: "Data",
      dataIndex: "date",
      sorter: true,
      key: "date",
      render: (_: any, record: any) =>
        record.date
          ? moment(record.date, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
    },
    {
      title: "Cliente",
      dataIndex: ["customer", "name"],
      sorter: true,
      key: "customer",
    },
    {
      title: "Vendedor",
      dataIndex: ["seller", "name"],
      sorter: true,
      key: "seller",
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Meio de Pagamento",
      dataIndex: "payment_method",
      sorter: true,
      key: "payment_method",
      render: (_: any, record: any) =>
        paymentMethods.find((method) => method.value === record.payment_method)
          ?.label || "",
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Valor",
      key: "total_amount",
      dataIndex: "total_amount",
      sorter: true,
      render: (_: any, record: any) => formatCurrency(record.total_amount || 0),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ações",
      key: "action",
      render: (_: any, record: any) =>
        profilePermissionService.hasPermission(`sales-sales:write`) && (
          <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
            <Tooltip placement="top" title="Detalhes">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<EyeOutlined />}
                onClick={() => onOpenDetails(record)}
              />
            </Tooltip>

            {!record.invoices.length && (
              <Spin spinning={generateInvoiceLoading === record.id}>
                <Tooltip placement="top" title="Gerar fatura(s)">
                  <Button
                    type="default"
                    shape="circle"
                    size="middle"
                    icon={<FileAddOutlined />}
                    onClick={() => {
                      if (!record.invoices.length) {
                        setGenerateInvoiceLoading(record.id);

                        generateInvoicesMutate({ saleUuid: record.uuid });
                      }
                    }}
                  />
                </Tooltip>
              </Spin>
            )}

            <Tooltip placement="top" title="Duplicar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<CopyOutlined />}
                onClick={() => toPage("duplicate", record.uuid)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Editar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<EditOutlined />}
                onClick={() => toPage("edit", record.uuid)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Apagar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => setRemoveSale(record)}
              />
            </Tooltip>
          </Space>
        ),
      width: "15%",
    },
  ];

  const filters = (
    <Form
      form={formFilter}
      name="filter_sales"
      layout="vertical"
      className="form_filter_horizontal"
      autoComplete="off"
      style={isMobile ? {} : { width: 300 }}
    >
      <Row gutter={20} style={{ width: "100%" }}>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Data"
            name="filter_date_range"
            className="item_filter"
          >
            <DatePicker.RangePicker
              size="large"
              format={"DD/MM/YYYY"}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Cliente"
            name="filter_customer"
            className="item_filter"
          >
            <Select
              placeholder="Todas"
              value={customerId}
              style={{ width: "100%" }}
              options={customers}
              size="large"
            />
          </Form.Item>
        </Col>

        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Meio de Pagamento"
            name="filter_payment_method"
            className="item_filter"
          >
            <Select
              placeholder="Todas"
              value={paymentMethod}
              style={{ width: "100%" }}
              options={allowedPaymentMethods}
              size="large"
            />
          </Form.Item>
        </Col>

        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Somente vendas de contratos"
            name="filter_has_contract"
            className="item_filter"
            valuePropName="checked"
          >
            <Switch size="default" />
          </Form.Item>
        </Col>

        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Button onClick={onFilter} size="large">
            Filtrar
          </Button>

          {(customerId ||
            paymentMethod ||
            startDate ||
            endDate ||
            hasContractOnly) && (
            <Button type="link" onClick={onClearFilters} size="large">
              Limpar
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  );

  const exportData = () => downloadExcel(salesToExport, "Vendas");

  const onMapDataToExport = (data: any) => {
    return data.map((record: any) => {
      return {
        Data: record.data.date
          ? moment(record.data.date, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
        Cliente: record.data.customer.name,
        Vendedor: record.data.seller.name,
        "Meio de Pagamento":
          paymentMethods.find(
            (method) => method.value === record.data.payment_method
          )?.label || "",
        Desconto: record.data.discount || 0,
        "Valor Total": formatCurrency(record.data.total_amount || 0),
        Serviços: record.data.saleServices
          .map((saleService: any) => {
            return `${saleService.service.name} (Qtd: ${
              saleService.quantity
            }, Valor: ${formatCurrency(saleService.price)})`;
          })
          .join("; "),
        "Data de Vencimento": record.data.due_date
          ? moment(record.data.due_date, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
        Parcelamento: record.data.installments,
        Situação:
          salesStatus.find((_status) => _status.value === record.data.status)
            ?.label || "",
      };
    });
  };

  useEffect(() => {
    if (salesToExport.length) {
      exportData();
    }

    setExportIsLoading(false);
  }, [salesToExport]);

  useEffect(() => {
    formFilter.setFieldsValue({
      filter_date_range: null,
    });
  }, []);

  useEffect(() => {
    formFilter.setFieldsValue({
      filter_date_range:
        startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null,
    });

    if (!startDate || !endDate) {
      formFilter.setFieldValue("filter_date_range", null);
    }
  }, [startDate, endDate]);

  return (
    <>
      <Card styles={{ body: { padding: 8 } }}>
        <Row>
          <Col
            lg={{ span: 12 }}
            md={{ span: 12 }}
            sm={{ span: 24 }}
            xs={{ span: 24 }}
          >
            <Form
              form={formSearch}
              name="search_sales"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item name="search_sale">
                <Search
                  onSearch={onSearch}
                  placeholder="Pesquisar venda"
                  enterButton="Pesquisar"
                  allowClear
                  size="large"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </Col>

          {profilePermissionService.hasPermission("sales-sales:write") && (
            <Col
              lg={{ span: 4, offset: 8 }}
              md={{ span: 4, offset: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Button
                type="primary"
                onClick={() => toPage("create")}
                size="large"
                block
              >
                Criar venda
              </Button>
            </Col>
          )}
        </Row>

        <Divider />

        <div style={{ marginBottom: 16 }}>
          {startDate && (
            <Tag
              closable
              onClose={() => {
                setStartDate("");
                formFilter.setFieldValue("filter_start_date", "");
                setCurrentPage(1);
              }}
            >
              Data Início: {moment(startDate).format("DD/MM/YYYY")}
            </Tag>
          )}
          {endDate && (
            <Tag
              closable
              onClose={() => {
                setEndDate("");
                formFilter.setFieldValue("filter_end_date", "");
                setCurrentPage(1);
              }}
            >
              Data Fim: {moment(endDate).format("DD/MM/YYYY")}
            </Tag>
          )}
          {customerId && (
            <Tag
              closable
              onClose={() => {
                setCustomerId("");
                formFilter.setFieldValue("filter_customer", "");
                setCurrentPage(1);
              }}
            >
              Cliente:{" "}
              {customers.find((c: any) => c.value === customerId)?.label}
            </Tag>
          )}
          {paymentMethod && (
            <Tag
              closable
              onClose={() => {
                setPaymentMethod("");
                formFilter.setFieldValue("filter_payment_method", "");
                setCurrentPage(1);
              }}
            >
              Meio de Pagamento:{" "}
              {
                allowedPaymentMethods.find(
                  (m: any) => m.value === paymentMethod
                )?.label
              }
            </Tag>
          )}
          {hasContractOnly && (
            <Tag
              closable
              onClose={() => {
                setHasContractOnly(false);
                formFilter.setFieldValue("filter_has_contract", false);
                setCurrentPage(1);
              }}
            >
              Somente vendas de contratos
            </Tag>
          )}
        </div>

        <div className="sale">
          <GenericTable
            columns={columns}
            dataSource={sales}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              showSizeChanger: true,
              total: total,
            }}
            loading={saleLoading}
            totalChildren={
              <div className="" style={isMobile ? {} : { marginTop: 8 }}>
                <Row>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    style={isMobile ? { marginBottom: 10, marginTop: 10 } : {}}
                  >
                    <ShowDataLength size={total} className="show_result_size" />
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    style={{
                      display: "flex",
                      justifyContent: isMobile ? "center" : "flex-end",
                    }}
                  >
                    <Space direction={isMobile ? "vertical" : "horizontal"}>
                      <FilterButton filters={filters} />

                      <ImportBtn
                        uploadFunction={importSales}
                        block={isMobile}
                        multiple={false}
                        onSuccess={() => {
                          AlertService.sendAlert([
                            { text: "Importação concluída com sucesso." },
                          ]);

                          loadSales();
                        }}
                      />

                      <ExportBtn
                        disabled={!total}
                        loading={exportIsLoading}
                        block={isMobile}
                        onClick={() => {
                          if (salesToExport.length) {
                            exportData();
                          } else {
                            setExportIsLoading(true);
                            loadSalesToExport();
                          }
                        }}
                      />
                    </Space>
                  </Col>
                </Row>
              </div>
            }
            empty={{
              createTheFirst: !sales.length && !search,
              textOne:
                "Esta venda encontra-se vazia, uma vez que ainda não foram criadas vendas.",
              textTwo: profilePermissionService.hasPermission(
                `sales-sales:write`
              )
                ? `Para começar, crie uma nova venda.`
                : "",
              link: profilePermissionService.hasPermission(`sales-sales:write`)
                ? `/sales/create`
                : "",
              createText: "Criar venda",
            }}
            handleTableChange={handleTableChange}
          />

          <DetailSale
            details={details}
            openDetails={openDetails}
            onCloseDetails={onCloseDetails}
          />

          <ConfirmModal
            open={!!removeSale}
            title={`Tem certeza que deseja apagar a venda "${removeSale?.name}"?`}
            okText="Apagar"
            cancelText="Voltar"
            onOk={() => removeSaleMutate(removeSale?.uuid)}
            onCancel={() => setRemoveSale(undefined)}
          ></ConfirmModal>
        </div>
      </Card>
    </>
  );
}
